import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Plus, Eye, Trash, CheckCircle, Warning, Package, Cube, Clock } from "@phosphor-icons/react";
import { Modal, Field } from "./Suppliers";
import { toast } from "sonner";

export default function AssemblyRuns() {
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.role === "operator";
  const canDelete = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [parts, setParts] = useState([]);
  const [inv, setInv] = useState({ batches: [], by_part: [] });
  const [open, setOpen] = useState(false);
  // Table filters
  const [filterProduct, setFilterProduct] = useState("");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const navigate = useNavigate();

  const load = async () => {
    const [r, p, i] = await Promise.all([
      api.get("/assembly-runs"), api.get("/parts"), api.get("/inventory"),
    ]);
    setItems(r.data); setParts(p.data); setInv(i.data);
  };
  useEffect(() => { load(); }, []);

  const assembledParts = parts.filter((p) => p.is_assembled && (p.bom || []).length > 0);

  const remove = async (id) => {
    if (!window.confirm("Delete this assembly run? Consumed stock will be REFUNDED back to the source GRN batches, and any auto-created boxes will be removed.")) return;
    try { await api.delete(`/assembly-runs/${id}`); toast.success("Deleted & stock refunded"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const [completing, setCompleting] = useState(null); // run object

  // Distinct products from current runs for the dropdown (id -> {id, label}).
  const productOptions = useMemo(() => {
    const map = new Map();
    for (const r of items) {
      const pid = r.product_part_id || r.product_part_number;
      if (!pid || map.has(pid)) continue;
      map.set(pid, { id: pid, label: `${r.product_part_number || ""} — ${r.product_name || ""}`.trim() });
    }
    return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [items]);

  const filteredItems = useMemo(() => {
    return items.filter((r) => {
      if (filterProduct) {
        const pid = r.product_part_id || r.product_part_number;
        if (pid !== filterProduct) return false;
      }
      if (filterStatus) {
        const s = r.status || "completed";
        if (s !== filterStatus) return false;
      }
      const d = r.run_date || r.received_date || "";
      if (filterDateFrom && d && d < filterDateFrom) return false;
      if (filterDateTo && d && d > filterDateTo) return false;
      return true;
    });
  }, [items, filterProduct, filterStatus, filterDateFrom, filterDateTo]);

  const hasFilters = filterProduct || filterStatus || filterDateFrom || filterDateTo;
  const clearFilters = () => {
    setFilterProduct(""); setFilterStatus(""); setFilterDateFrom(""); setFilterDateTo("");
  };

  return (
    <div className="space-y-6" data-testid="assembly-runs-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-4xl uppercase tracking-tighter">Assembly Runs</h1>
          <p className="text-xs text-zinc-600 mt-1">In-house assembly · Start → In-Progress → Complete (QA rejections) → Boxes</p>
        </div>
        {canEdit && (
          <button onClick={() => setOpen(true)} className="brut-btn brut-btn-primary px-4 py-2 inline-flex items-center gap-2" data-testid="new-run-btn">
            <Plus size={14} weight="bold" /> Start Assembly Run
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="brut-card p-3" data-testid="runs-filters">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
          <div className="md:col-span-2">
            <label className="brut-label">Product</label>
            <select value={filterProduct} onChange={(e) => setFilterProduct(e.target.value)} className="brut-input mt-1" data-testid="filter-product">
              <option value="">All products</option>
              {productOptions.map((p) => (
                <option key={p.id} value={p.id}>{p.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="brut-label">Status</label>
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="brut-input mt-1" data-testid="filter-status">
              <option value="">All</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
            </select>
          </div>
          <div>
            <label className="brut-label">From</label>
            <input type="date" value={filterDateFrom} onChange={(e) => setFilterDateFrom(e.target.value)} className="brut-input mt-1" data-testid="filter-date-from" />
          </div>
          <div>
            <label className="brut-label">To</label>
            <input type="date" value={filterDateTo} onChange={(e) => setFilterDateTo(e.target.value)} className="brut-input mt-1" data-testid="filter-date-to" />
          </div>
        </div>
        <div className="flex items-center justify-between mt-3">
          <div className="text-xs text-zinc-600 font-mono-ui" data-testid="filter-count">
            Showing <b>{filteredItems.length}</b> of <b>{items.length}</b> run{items.length === 1 ? "" : "s"}
          </div>
          {hasFilters && (
            <button onClick={clearFilters} className="brut-tag brut-tag-white text-xs" data-testid="clear-filters">
              Clear filters
            </button>
          )}
        </div>
      </div>

      <div className="brut-card overflow-x-auto">
        <table className="brut-table" data-testid="runs-table">
          <thead><tr><th>Run #</th><th>Product</th><th>Type</th><th>Project</th><th>Target</th><th>Status</th><th>Duration</th><th>Boxes</th><th>Date</th><th></th></tr></thead>
          <tbody>
            {filteredItems.length === 0 && <tr><td colSpan={10} className="text-center py-8 text-zinc-500">{items.length === 0 ? "No assembly runs yet." : "No runs match the current filters."}</td></tr>}
            {filteredItems.map((r) => {
              const line = r.lines?.[0];
              const isShippable = !!r.is_shippable_product;
              const status = r.status || "completed";
              const durMin = r.duration_seconds ? Math.round(r.duration_seconds / 60) : null;
              return (
                <tr key={r.id} data-testid={`run-row-${r.grn_number}`}>
                  <td className="font-bold">
                    <Link to={`/assembly-runs/${r.id}`} className="underline">{r.grn_number}</Link>
                  </td>
                  <td>
                    <div className="font-mono-ui text-xs">{r.product_part_number}</div>
                    <div className="text-xs text-zinc-600">{r.product_name}</div>
                  </td>
                  <td>
                    {isShippable
                      ? <span className="brut-tag brut-tag-green">Shippable</span>
                      : <span className="brut-tag brut-tag-dark">Sub-Assembly</span>}
                  </td>
                  <td className="text-xs">{r.target_project || (isShippable ? "—" : "Stock")}</td>
                  <td className="font-mono-ui font-bold">
                    {r.target_units || r.units_produced}
                    {line && !isShippable && <span className="text-xs text-zinc-500 ml-1">({line.qty_remaining} left)</span>}
                  </td>
                  <td>
                    {status === "in_progress" ? (
                      <div>
                        <span className="brut-tag brut-tag-yellow inline-flex items-center gap-1"><Clock size={10} weight="bold" /> In Progress</span>
                        {r.cumulative_produced > 0 && (
                          <div className="mt-1" data-testid={`progress-${r.grn_number}`}>
                            <div className="h-1.5 bg-slate-200 border border-slate-300">
                              <div className="h-full bg-blue-600" style={{ width: `${Math.min(100, (r.cumulative_produced / (r.target_units || 1)) * 100)}%` }} />
                            </div>
                            <div className="text-[10px] font-mono-ui text-zinc-600 mt-0.5">
                              {r.cumulative_produced}/{r.target_units} · {Math.round((r.cumulative_produced / (r.target_units || 1)) * 100)}%
                            </div>
                          </div>
                        )}
                      </div>
                    ) : <span className="brut-tag brut-tag-green inline-flex items-center gap-1"><CheckCircle size={10} weight="bold" /> Completed</span>}
                  </td>
                  <td className="font-mono-ui text-xs">{durMin != null ? `${durMin} min` : "—"}</td>
                  <td className="font-mono-ui">
                    {isShippable ? (r.box_ids || []).length : "—"}
                  </td>
                  <td className="font-mono-ui">{r.run_date || r.received_date}</td>
                  <td className="text-right whitespace-nowrap">
                    {status === "in_progress" && canEdit && (
                      <button onClick={() => setCompleting(r)} className="brut-tag brut-tag-dark mr-1" data-testid={`complete-run-${r.grn_number}`}>
                        Complete
                      </button>
                    )}
                    <Link to={`/assembly-runs/${r.id}`} className="brut-tag brut-tag-yellow mr-1" data-testid={`view-run-${r.grn_number}`}>
                      <Eye size={10} weight="bold" />
                    </Link>
                    {canDelete && (
                      <button onClick={() => remove(r.id)} className="brut-tag brut-tag-red" data-testid={`delete-run-${r.grn_number}`}>
                        <Trash size={10} weight="bold" />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {completing && (
        <CompleteRunModal
          run={completing}
          parts={parts}
          inv={inv}
          onClose={() => setCompleting(null)}
          onSaved={() => { setCompleting(null); load(); }}
        />
      )}

      {open && (
        <NewAssemblyRun
          parts={parts}
          assembledParts={assembledParts}
          inv={inv}
          onClose={() => setOpen(false)}
          onSaved={() => { setOpen(false); load(); }}
        />
      )}
    </div>
  );
}

function NewAssemblyRun({ parts, assembledParts, inv, onClose, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const [productId, setProductId] = useState(assembledParts[0]?.id || "");
  const [target, setTarget] = useState(1);
  const [runDate, setRunDate] = useState(today);
  const [targetProject, setTargetProject] = useState("");
  const [remarks, setRemarks] = useState("");
  const [allocations, setAllocations] = useState({});

  const product = assembledParts.find((p) => p.id === productId);
  const bom = product?.bom || [];
  const isShippable = !!product?.is_shippable;
  const perBox = product?.assemblies_per_box || 0;
  const targetN = parseInt(target, 10) || 0;
  const numBoxes = isShippable && perBox > 0 ? Math.ceil(targetN / perBox) : 0;
  const lastBoxUnits = isShippable && perBox > 0 && numBoxes > 0 ? (targetN - perBox * (numBoxes - 1)) : 0;
  const hasPartialBox = isShippable && perBox > 0 && targetN % perBox !== 0;
  const shippableConfigured = !isShippable || perBox > 0;
  const targetValid = !isShippable || (perBox > 0 && targetN > 0);

  const required = useMemo(() => {
    const r = {};
    for (const item of bom) r[item.sub_part_id] = item.qty_per_assembly * targetN;
    return r;
  }, [bom, targetN]);

  const allocated = useMemo(() => {
    const a = {};
    Object.entries(allocations).forEach(([pid, allocs]) => {
      a[pid] = (allocs || []).reduce((acc, x) => acc + (parseFloat(x.qty) || 0), 0);
    });
    return a;
  }, [allocations]);

  const allMatch = bom.length > 0 && bom.every((b) => Math.abs((allocated[b.sub_part_id] || 0) - required[b.sub_part_id]) < 1e-6);

  useEffect(() => {
    if (!productId) return;
    const p = assembledParts.find((x) => x.id === productId);
    const empty = {};
    (p?.bom || []).forEach((b) => { empty[b.sub_part_id] = [{ grn_line_id: "", qty: 0 }]; });
    setAllocations(empty);
    if (p?.is_shippable && p?.assemblies_per_box > 0) setTarget(p.assemblies_per_box);
    else setTarget(1);
    // Sub-assembly project is always "Stock"; shippable starts blank for user to fill
    setTargetProject(p?.is_shippable ? "" : "Stock");
    // eslint-disable-next-line
  }, [productId]);

  const batchesFor = (subPartId) => inv.batches.filter((b) => b.part_id === subPartId);

  const updateAlloc = (sub, idx, key, val) =>
    setAllocations({ ...allocations, [sub]: allocations[sub].map((a, i) => (i === idx ? { ...a, [key]: val } : a)) });
  const addAlloc = (sub) =>
    setAllocations({ ...allocations, [sub]: [...(allocations[sub] || []), { grn_line_id: "", qty: 0 }] });
  const removeAlloc = (sub, idx) =>
    setAllocations({ ...allocations, [sub]: allocations[sub].filter((_, i) => i !== idx) });

  const autoAllocate = () => {
    const next = {};
    for (const b of bom) {
      const req = b.qty_per_assembly * targetN;
      const batches = batchesFor(b.sub_part_id).slice().sort((x, y) => (x.received_date || "").localeCompare(y.received_date || ""));
      let remaining = req;
      const allocs = [];
      for (const batch of batches) {
        if (remaining <= 0) break;
        const take = Math.min(remaining, batch.qty_remaining);
        if (take > 0) {
          allocs.push({ grn_line_id: `${batch.grn_id}:${batch.line_id}`, qty: take });
          remaining -= take;
        }
      }
      if (allocs.length === 0) allocs.push({ grn_line_id: "", qty: 0 });
      next[b.sub_part_id] = allocs;
    }
    setAllocations(next);
    toast.success("Auto-allocated FIFO");
  };

  const save = async (e) => {
    e.preventDefault();
    if (!productId) return toast.error("Select a product");
    if (!targetN) return toast.error("Target units required");
    if (isShippable && !perBox) return toast.error(`Configure 'assemblies_per_box' on ${product.part_number} first`);
    if (isShippable && !targetProject.trim()) return toast.error("Target Project Name is required for shippable runs");
    if (!allMatch) return toast.error("Allocations must match BoM");

    const components = [];
    Object.entries(allocations).forEach(([pid, allocs]) => {
      allocs.forEach((a) => {
        if (a.grn_line_id && a.qty > 0) {
          components.push({ part_id: pid, grn_line_id: a.grn_line_id, qty: parseFloat(a.qty) });
        }
      });
    });

    try {
      const { data } = await api.post("/assembly-runs", {
        product_part_id: productId,
        target_units: targetN,
        run_date: runDate,
        target_project: isShippable ? targetProject.trim() : "Stock",
        remarks,
        components,
      });
      const msg = isShippable
        ? `Run ${data.grn_number} started (in progress) · complete to create ${numBoxes} boxes`
        : `Run ${data.grn_number} started (in progress) for ${targetN} ${product.part_number}`;
      toast.success(msg);
      onSaved(data);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <Modal title="Start Assembly Run" onClose={onClose}>
      {assembledParts.length === 0 ? (
        <div className="brut-tag brut-tag-red w-full text-center" data-testid="no-assembly-warning">
          No assembled parts with BoM defined. Go to Parts and add one.
        </div>
      ) : (
        <form onSubmit={save} className="space-y-4" data-testid="run-form">
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <label className="brut-label">Assembled Product *</label>
              <select required value={productId} onChange={(e) => setProductId(e.target.value)} className="brut-input mt-1" data-testid="run-product">
                {assembledParts.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.part_number} · {p.name}{p.is_shippable ? "  (Shippable)" : "  (Sub-Assembly)"}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="brut-label">{isShippable ? "Target Units" : "Units Produced"} *</label>
              <input
                type="number"
                min="1"
                step="1"
                required
                value={target}
                onChange={(e) => setTarget(e.target.value)}
                className="brut-input mt-1"
                data-testid="run-target"
              />
              {isShippable && perBox > 0 && targetN > 0 && (
                <div className="text-[10px] text-zinc-600 mt-1">
                  {hasPartialBox ? (
                    <>
                      → <b>{numBoxes}</b> box{numBoxes === 1 ? "" : "es"} ({numBoxes - 1} × {perBox} + <b>1 partial × {lastBoxUnits}</b>)
                    </>
                  ) : (
                    <>
                      → <b>{numBoxes}</b> full box{numBoxes === 1 ? "" : "es"} × {perBox}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
          <Field label="Run Date" type="date" value={runDate} onChange={setRunDate} testid="run-date" />

          {isShippable ? (
            <Field label="Target Project Name *" required value={targetProject} onChange={setTargetProject} testid="run-project" placeholder="e.g. Nissan-Q3-2026" />
          ) : (
            <div>
              <label className="brut-label">Target Project</label>
              <input value="Stock" disabled className="brut-input mt-1 opacity-70" data-testid="run-project-stock" />
              <p className="text-[10px] text-zinc-500 mt-1">Sub-assembly runs always go to Stock.</p>
            </div>
          )}

          {!shippableConfigured && (
            <div className="brut-tag brut-tag-red w-full text-center" data-testid="missing-per-box-warn">
              Set the Assemblies-per-Box field on {product.part_number} in the Parts page before starting a shippable run.
            </div>
          )}

          {isShippable && (
            <div className="brut-border p-3 bg-green-50">
              <div className="flex items-center justify-between">
                <div className="brut-label flex items-center gap-1"><Cube size={12} weight="bold" /> Boxes to be auto-created</div>
                <div className="font-display text-2xl">{numBoxes}</div>
              </div>
              <p className="text-[10px] text-zinc-600 mt-1">Each box will contain {perBox} units of {product.part_number}, tied back to this run with a QR-coded slip.</p>
            </div>
          )}

          <div className="brut-border p-3 bg-[#eff6ff]">
            <div className="flex justify-between items-center mb-2">
              <div>
                <div className="brut-label">Required from BoM</div>
                <p className="text-[10px] text-zinc-600">{bom.length} sub-parts × {targetN} units</p>
              </div>
              <button type="button" onClick={autoAllocate} className="brut-tag brut-tag-dark" data-testid="run-auto-allocate">
                Auto-Allocate FIFO
              </button>
            </div>
            <div className="space-y-3">
              {bom.map((b) => {
                const sub = parts.find((p) => p.id === b.sub_part_id);
                const batches = batchesFor(b.sub_part_id);
                const totalAvail = batches.reduce((acc, x) => acc + x.qty_remaining, 0);
                const req = required[b.sub_part_id] || 0;
                const allo = allocated[b.sub_part_id] || 0;
                const ok = Math.abs(allo - req) < 1e-6;
                return (
                  <div key={b.sub_part_id} className="brut-border bg-white p-3" data-testid={`req-${sub?.part_number}`}>
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="font-bold font-mono-ui text-sm">
                          {sub?.part_number}
                          {sub?.is_assembled && <span className="brut-tag brut-tag-dark ml-1 text-[9px]">SUB-ASSEMBLY</span>}
                        </div>
                        <div className="text-xs text-zinc-600">{sub?.name}</div>
                        <div className="text-[10px] mt-1">
                          <span className="brut-tag brut-tag-white">{b.qty_per_assembly} per unit</span>
                          <span className="brut-tag brut-tag-yellow ml-1">Required: {req}</span>
                          <span className={`brut-tag ml-1 ${totalAvail >= req ? "brut-tag-green" : "brut-tag-red"}`}>Available: {totalAvail}</span>
                        </div>
                      </div>
                      <div className={`brut-tag ${ok ? "brut-tag-green" : "brut-tag-red"} inline-flex items-center gap-1`}>
                        {ok ? <CheckCircle size={10} weight="bold" /> : <Warning size={10} weight="bold" />}
                        Allocated: {allo}/{req}
                      </div>
                    </div>
                    <div className="space-y-2 mt-3">
                      {(allocations[b.sub_part_id] || []).map((a, i) => (
                        <div key={i} className="grid grid-cols-12 gap-2 items-end" data-testid={`alloc-${sub?.part_number}-${i}`}>
                          <div className="col-span-8">
                            <label className="brut-label">Batch</label>
                            <select value={a.grn_line_id} onChange={(e) => updateAlloc(b.sub_part_id, i, "grn_line_id", e.target.value)} className="brut-input mt-1">
                              <option value="">— Select —</option>
                              {batches.map((batch) => (
                                <option key={batch.line_id} value={`${batch.grn_id}:${batch.line_id}`}>
                                  {batch.grn_number} · {batch.supplier_name} · Avail: {batch.qty_remaining}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="col-span-3">
                            <label className="brut-label">Qty</label>
                            <input type="number" min="0" step="any" value={a.qty} onChange={(e) => updateAlloc(b.sub_part_id, i, "qty", parseFloat(e.target.value) || 0)} className="brut-input mt-1" />
                          </div>
                          <div className="col-span-1 flex justify-end">
                            <button type="button" onClick={() => removeAlloc(b.sub_part_id, i)} className="brut-tag brut-tag-red">
                              <Trash size={10} weight="bold" />
                            </button>
                          </div>
                        </div>
                      ))}
                      <button type="button" onClick={() => addAlloc(b.sub_part_id)} className="brut-tag brut-tag-white inline-flex items-center gap-1">
                        <Plus size={10} weight="bold" /> Another batch
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <Field label="Remarks" value={remarks} onChange={setRemarks} textarea testid="run-remarks" />

          <button type="submit" disabled={!allMatch || !targetValid} className="brut-btn brut-btn-primary px-4 py-2 disabled:opacity-50" data-testid="save-run-btn">
            {!targetValid ? "Target units required" : (!allMatch ? "Allocations must match BoM" : "Start Run")}
          </button>
        </form>
      )}
    </Modal>
  );
}


function CompleteRunModal({ run, parts, inv, onClose, onSaved }) {
  // QA rejections per BoM sub-part — each is consumed from a chosen batch (extra above original allocation)
  const product = parts.find((p) => p.id === run.product_part_id);
  const bom = product?.bom || [];
  const [rows, setRows] = useState(
    bom.map((b) => ({
      part_id: b.sub_part_id,
      grn_line_id: "",
      qty_rejected: 0,
      reason: "",
    }))
  );
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const batchesFor = (partId) => inv.batches.filter((x) => x.part_id === partId);

  const updateRow = (i, key, val) => setRows(rows.map((r, idx) => (idx === i ? { ...r, [key]: val } : r)));

  const submit = async (e) => {
    e.preventDefault();
    // Build payload — only rows with qty_rejected > 0 are sent
    const qa_rejections = rows
      .filter((r) => parseFloat(r.qty_rejected) > 0)
      .map((r) => {
        if (!r.grn_line_id) {
          toast.error("Pick a batch for every rejected qty row");
          throw new Error("missing batch");
        }
        return {
          part_id: r.part_id,
          grn_line_id: r.grn_line_id,
          qty_rejected: parseFloat(r.qty_rejected),
          reason: r.reason || "",
        };
      });
    setBusy(true);
    try {
      const { data } = await api.post(`/assembly-runs/${run.id}/complete`, { qa_rejections, notes });
      toast.success(`Run ${data.grn_number} completed in ${Math.round((data.duration_seconds || 0) / 60)} min`);
      onSaved(data);
    } catch (err) {
      if (err?.message !== "missing batch") toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  return (
    <Modal title={`Complete Run ${run.grn_number}`} onClose={onClose}>
      <form onSubmit={submit} className="space-y-4" data-testid="complete-form">
        <div className="brut-border p-3 bg-[#eff6ff] text-xs">
          <div className="font-display uppercase text-sm">{run.product_part_number} · {run.product_name}</div>
          <div className="mt-1">Target: <b>{run.target_units}</b> · Project: <b>{run.target_project || "Stock"}</b></div>
          <p className="mt-1 text-zinc-600">Enter QA rejection qty per sub-part. Each rejection additionally consumes stock from the chosen batch. Output count remains {run.target_units} (rejected items are replaced from inventory).</p>
        </div>

        <div className="space-y-2">
          {rows.length === 0 ? (
            <div className="text-xs text-zinc-500 brut-border p-3 text-center">No BoM defined.</div>
          ) : rows.map((r, i) => {
            const sub = parts.find((p) => p.id === r.part_id);
            const batches = batchesFor(r.part_id);
            return (
              <div key={i} className="brut-border p-3" data-testid={`qa-row-${sub?.part_number}`}>
                <div className="flex justify-between items-start">
                  <div>
                    <div className="font-bold font-mono-ui text-sm">{sub?.part_number}</div>
                    <div className="text-xs text-zinc-600">{sub?.name}</div>
                  </div>
                </div>
                <div className="grid grid-cols-12 gap-2 items-end mt-2">
                  <div className="col-span-3">
                    <label className="brut-label">Rejected Qty</label>
                    <input type="number" min="0" step="any" value={r.qty_rejected} onChange={(e) => updateRow(i, "qty_rejected", e.target.value)} className="brut-input mt-1" data-testid={`qa-qty-${sub?.part_number}`} />
                  </div>
                  <div className="col-span-5">
                    <label className="brut-label">Replace from Batch</label>
                    <select value={r.grn_line_id} onChange={(e) => updateRow(i, "grn_line_id", e.target.value)} className="brut-input mt-1" data-testid={`qa-batch-${sub?.part_number}`}>
                      <option value="">— Select —</option>
                      {batches.map((batch) => (
                        <option key={batch.line_id} value={`${batch.grn_id}:${batch.line_id}`}>
                          {batch.grn_number} · {batch.supplier_name} · Avail: {batch.qty_remaining}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-span-4">
                    <label className="brut-label">Reason</label>
                    <input value={r.reason} onChange={(e) => updateRow(i, "reason", e.target.value)} placeholder="e.g. bent edges" className="brut-input mt-1" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div>
          <label className="brut-label">Completion Notes</label>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="brut-input mt-1" data-testid="qa-notes" />
        </div>

        <button type="submit" disabled={busy} className="brut-btn brut-btn-primary px-4 py-2 disabled:opacity-50" data-testid="finish-run-btn">
          {busy ? "Finishing…" : "Finish Assembly Run"}
        </button>
      </form>
    </Modal>
  );
}
